
import React, { useState, useEffect, useRef } from 'react';
import { ICONS } from './constants';
import { AppMode, GeneratedImage, TransformationConfig, VariationItem } from './types';
import Controls from './components/Controls';
import History from './components/Lightbox'; 
import ComparisonView from './components/ComparisonView';
import ImageViewer from './components/ImageViewer';
import LandingPage from './components/LandingPage';
import Profile from './components/Profile';
import SubscriptionModal from './components/SubscriptionModal';
import { Onboarding } from './components/Onboarding'; 
import { TutorialsPage } from './components/TutorialsPage'; 
import * as geminiService from './services/geminiService';
import * as storageService from './services/storageService';
import { auth, createUserDocument, subscribeToUserDocument, manageCredits, deductCredit, setupSubscriptionListener, completeOnboarding } from './services/firebase'; 
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';

function App() {
  const [mode, setMode] = useState<AppMode>(() => {
      try {
          const savedMode = sessionStorage.getItem('reshooter_app_mode');
          if (savedMode === AppMode.DASHBOARD) return AppMode.UPLOAD;
          return (savedMode as AppMode) || AppMode.LANDING;
      } catch (e) {
          return AppMode.LANDING;
      }
  });

  const [previousMode, setPreviousMode] = useState<AppMode>(AppMode.UPLOAD);
  
  const [user, setUser] = useState<any>(null); 
  const [reshoots, setReshoots] = useState<number>(0);
  const [showSubscription, setShowSubscription] = useState(false);
  const [userPlan, setUserPlan] = useState<string>('trial');
  const [isAdmin, setIsAdmin] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  const onboardingTriggeredRef = useRef(false);

  // --- SESSION STATE ---
  const [mainImage, setMainImage] = useState<File | null>(null);
  const [mainImagePreview, setMainImagePreview] = useState<string | null>(null);
  const [resultImage, setResultImage] = useState<string | null>(null);
  const [resultId, setResultId] = useState<string | null>(null); 
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [historyItems, setHistoryItems] = useState<GeneratedImage[]>([]);
  
  const mobileWrapperRef = useRef<HTMLDivElement>(null);

  const [variations, setVariations] = useState<VariationItem[]>([]);
  const [isGeneratingVariations, setIsGeneratingVariations] = useState(false);
  const [savedUrls, setSavedUrls] = useState<Set<string>>(new Set());
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  const [showControls, setShowControls] = useState(false);

  const [isAutoTuning, setIsAutoTuning] = useState(false);
  const [critique, setCritique] = useState<string | null>(null);

  const [viewingImageIndex, setViewingImageIndex] = useState<number | null>(null);
  const [viewingContext, setViewingContext] = useState<'HISTORY' | 'VARIATIONS' | 'RESULT' | null>(null);
  
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  
  const [config, setConfig] = useState<TransformationConfig>({
    backgroundMode: 'keep',
    propMode: 'keep',
    aspectRatioMode: 'keep' 
  });

  const dataURLtoFile = (dataurl: string, filename: string): File => {
    try {
        const arr = dataurl.split(',');
        const match = arr[0].match(/:(.*?);/);
        const mime = match ? match[1] : 'image/png';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while(n--){ u8arr[n] = bstr.charCodeAt(n); }
        return new File([u8arr], filename, {type:mime});
    } catch (e) {
        return new File([], filename, { type: 'image/png' });
    }
  };

  useEffect(() => {
    try {
        localStorage.removeItem('reshooter_session_source');
        localStorage.removeItem('reshooter_session_result');
        localStorage.removeItem('reshooter_session_config');
    } catch (e) {}
  }, []);

  useEffect(() => {
      const safeSetItem = (key: string, value: string) => {
          try { sessionStorage.setItem(key, value); } catch (e) {}
      };
      if (mainImagePreview && mainImagePreview.length < 500000) safeSetItem('reshooter_session_source', mainImagePreview);
      else sessionStorage.removeItem('reshooter_session_source');
      if (resultImage && resultImage.length < 500000) safeSetItem('reshooter_session_result', resultImage);
      else sessionStorage.removeItem('reshooter_session_result');
      safeSetItem('reshooter_session_config', JSON.stringify(config));
  }, [mainImagePreview, resultImage, config]);

  useEffect(() => {
      if (mode !== AppMode.LANDING) {
          try { sessionStorage.setItem('reshooter_app_mode', mode); } catch (e) {}
      }
  }, [mode]);

  useEffect(() => {
      try {
          const sSource = sessionStorage.getItem('reshooter_session_source');
          if (sSource && sSource.length > 500000) sessionStorage.removeItem('reshooter_session_source');
          const sResult = sessionStorage.getItem('reshooter_session_result');
          if (sResult && sResult.length > 500000) sessionStorage.removeItem('reshooter_session_result');
      } catch(e) {}
      try {
          const savedConfig = sessionStorage.getItem('reshooter_session_config');
          if (savedConfig) { try { setConfig(JSON.parse(savedConfig)); } catch (e) {} }
          const savedSource = sessionStorage.getItem('reshooter_session_source');
          if (savedSource) {
              setMainImagePreview(savedSource);
              setMainImage(dataURLtoFile(savedSource, "restored_session_image.png"));
          }
          const savedResult = sessionStorage.getItem('reshooter_session_result');
          if (savedResult) { setResultImage(savedResult); }
      } catch (e) {
          sessionStorage.clear();
      }
  }, []);

  useEffect(() => {
    if (!auth) return;
    let userUnsubscribe: () => void;
    let subListener: () => void;
    let historyUnsubscribe: () => void;

    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
        setUser(currentUser);
        if (currentUser && currentUser.emailVerified) {
            await createUserDocument(currentUser);
            await manageCredits(currentUser.uid);
            subListener = setupSubscriptionListener(currentUser.uid);

            userUnsubscribe = subscribeToUserDocument(currentUser.uid, (data) => {
                if (data) {
                    const isUserAdmin = data.isAdmin === true;
                    if (typeof data.credits === 'number') setReshoots(data.credits);
                    if (data.plan) setUserPlan(data.plan);
                    if (isUserAdmin) setIsAdmin(true);
                    
                    if (!onboardingTriggeredRef.current) {
                        if (data.hasCompletedOnboarding === false || isUserAdmin) {
                            setShowOnboarding(true);
                            onboardingTriggeredRef.current = true;
                        }
                    }
                }
            });

            historyUnsubscribe = storageService.subscribeToHistory(currentUser.uid, (items) => {
                setHistoryItems(items);
                const favs = new Set<string>();
                items.forEach(i => { if (i.isFavorite) favs.add(i.id); });
                setFavorites(favs);
            });
            
            setMode(prevMode => {
                if (prevMode === AppMode.LANDING || prevMode === AppMode.DASHBOARD) return AppMode.UPLOAD;
                return prevMode; 
            });

            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('payment') === 'success') {
                alert("Payment Successful! Welcome to Studio.");
                window.history.replaceState({}, '', window.location.pathname);
            }
        } else if (!currentUser) {
            setMode(AppMode.LANDING);
            onboardingTriggeredRef.current = false;
            sessionStorage.removeItem('reshooter_app_mode');
            sessionStorage.removeItem('reshooter_session_source');
            sessionStorage.removeItem('reshooter_session_result');
        }
    });

    return () => {
        unsubscribe();
        if (userUnsubscribe) userUnsubscribe();
        if (subListener) subListener();
        if (historyUnsubscribe) historyUnsubscribe();
    };
  }, []); 

  const handleOnboardingComplete = async () => {
      setShowOnboarding(false);
      if (user) { await completeOnboarding(user.uid); }
  };

  useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
          if (mode === AppMode.UPLOAD || mode === AppMode.RESULT) {
              if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                  e.preventDefault();
                  if (mainImage && !isProcessing) startReshoot();
              }
          }
          if (e.key === 'Escape') {
              if (mode === AppMode.LIGHTBOX) closeHistory();
              if (viewingImageIndex !== null) setViewingImageIndex(null);
          }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mode, mainImage, resultImage, isProcessing, viewingImageIndex]);

  useEffect(() => {
    if (mode === AppMode.RESULT && resultImage) {
        window.scrollTo(0, 0);
        if (mobileWrapperRef.current) mobileWrapperRef.current.scrollTop = 0;
        const timer = setTimeout(() => {
             window.scrollTo(0, 0);
             if (mobileWrapperRef.current) mobileWrapperRef.current.scrollTop = 0;
        }, 100);
        return () => clearTimeout(timer);
    }
  }, [mode, resultImage]);

  const handleMainImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setMainImage(file);
      setCritique(null);
      setConfig({ backgroundMode: 'keep', propMode: 'keep', aspectRatioMode: 'keep' });
      const reader = new FileReader();
      reader.onload = (ev) => { setMainImagePreview(ev.target?.result as string); };
      reader.readAsDataURL(file);
    }
  };
  
  const startReshoot = async (e?: React.MouseEvent) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!mainImage || !user) return;
    setIsProcessing(true);
    try {
      const hasCredit = await deductCredit(user.uid, 1);
      if (!hasCredit) {
          setIsProcessing(false);
          alert("Insufficient RESHOOTS! Please upgrade your plan.");
          setShowSubscription(true);
          return;
      }
      setVariations([]);
      setSavedUrls(new Set());
      const generatedBase64 = await geminiService.generateReshoot(mainImage, config, userPlan);
      setResultImage(generatedBase64);
      const newId = await saveToHistory(generatedBase64, "Generated", true);
      setResultId(newId);
      setMode(AppMode.RESULT);
    } catch (error: any) {
      alert("Transformation Failed. Please check your connection and try again.");
    } finally {
      setIsProcessing(false); 
    }
  };

  const handleGenerateVariations = async () => {
      const sourceImageStr = resultImage || mainImagePreview;
      if (!sourceImageStr || !user) return;
      const hasCredit = await deductCredit(user.uid, 3);
      if (!hasCredit) {
          alert("Insufficient RESHOOTS! Alternatives require 3 RESHOOTS.");
          setShowSubscription(true);
          return;
      }
      setIsGeneratingVariations(true);
      try {
          const file = dataURLtoFile(sourceImageStr, "variation_source.png");
          const variationConfig: TransformationConfig = {
              backgroundMode: 'keep', propMode: 'keep', aspectRatio: config.aspectRatio,
              aspectRatioMode: config.aspectRatioMode, framingMode: 'change', angleMode: 'change'
          };
          const vars = await geminiService.generateVariations(file, variationConfig, 3, userPlan, true);
          if (vars.length > 0) {
              const newVariations: VariationItem[] = [];
              for (const v of vars) {
                  const id = await saveToHistory(v, "Variation", true);
                  newVariations.push({ id, url: v, isFavorite: false });
              }
              setVariations(prev => [...prev, ...newVariations]);
          }
      } catch (e: any) {
          console.error("Failed to generate variations", e);
      } finally {
          setIsGeneratingVariations(false);
      }
  };

  const saveToHistory = async (imgData: string, labelPrefix: string = "Reshot", updateState: boolean = true): Promise<string> => {
    const id = Date.now().toString() + Math.random().toString().slice(2,5);
    const newItem: GeneratedImage = {
      id: id, url: imgData, timestamp: Date.now(), label: `${labelPrefix} ${new Date().toLocaleTimeString()}`, config: config, isFavorite: false
    };
    try {
      if (updateState) {
          setHistoryItems(prev => [newItem, ...prev]);
          setSavedUrls(prev => new Set(prev).add(imgData));
      }
      await storageService.saveToHistory(newItem, user?.uid);
      return id;
    } catch (e) {
      return id;
    }
  };

  const removeFromHistory = async (id: string) => {
    setHistoryItems(prev => prev.filter(item => item.id !== id));
    try { await storageService.removeFromHistory(id, user?.uid); } catch (e) {}
  };
  
  const handleBulkRemove = async (ids: string[]) => {
      setHistoryItems(prev => prev.filter(item => !ids.includes(item.id)));
      try { await storageService.bulkRemoveFromHistory(ids, user?.uid); } catch (e) {}
  };

  const toggleFavorite = async (id: string, isFav: boolean) => {
      setFavorites(prev => { const next = new Set(prev); if (isFav) next.add(id); else next.delete(id); return next; });
      setHistoryItems(prev => prev.map(item => item.id === id ? { ...item, isFavorite: isFav } : item));
      setVariations(prev => prev.map(item => item.id === id ? { ...item, isFavorite: isFav } : item));
      await storageService.toggleFavorite(id, isFav, user?.uid);
  };
  
  const handleBulkFavorite = async (ids: string[], isFav: boolean) => {
      setFavorites(prev => {
          const next = new Set(prev);
          ids.forEach(id => { if (isFav) next.add(id); else next.delete(id); });
          return next;
      });
      setHistoryItems(prev => prev.map(item => ids.includes(item.id) ? { ...item, isFavorite: isFav } : item));
      await storageService.bulkToggleFavorite(ids, isFav, user?.uid);
  };

  const handleSmartDownload = async (src: string, filename: string) => {
    try {
        const response = await fetch(src, { mode: 'cors', credentials: 'omit' });
        if (!response.ok) throw new Error("Fetch failed");
        const blob = await response.blob();
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        const objectURL = URL.createObjectURL(blob);
        img.onload = () => {
            canvas.width = img.width; canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);
            canvas.toBlob(async (newBlob) => {
                const finalBlob = newBlob || blob;
                const file = new File([finalBlob], filename, { type: 'image/png' });
                if (navigator.share && navigator.share && navigator.canShare({ files: [file] })) {
                    try { await navigator.share({ files: [file], title: 'Reshooter Image', text: 'Created with Reshooter' }); setTimeout(() => URL.revokeObjectURL(objectURL), 1000); return; } catch (shareError) {}
                }
                const finalUrl = URL.createObjectURL(finalBlob);
                const link = document.createElement('a');
                link.href = finalUrl; link.download = filename;
                document.body.appendChild(link); link.click(); document.body.removeChild(link);
                setTimeout(() => { URL.revokeObjectURL(objectURL); URL.revokeObjectURL(finalUrl); }, 1000);
            }, 'image/png');
        };
        img.src = objectURL;
    } catch (e) {
        const link = document.createElement('a');
        link.href = src; link.download = filename;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
    }
  };

  const handlePushProcess = (item: GeneratedImage) => {
      try {
          const file = dataURLtoFile(item.url, "pushed-process.png");
          setMainImage(file); setMainImagePreview(item.url);
          if (item.config) setConfig(item.config);
          setResultImage(null); setResultId(null); setVariations([]); setSavedUrls(new Set()); setCritique(null);
          closeViewer(); setMode(AppMode.UPLOAD);
      } catch (e) { alert("Could not process this image."); }
  };
  
  const openHistory = () => { setPreviousMode(mode); setMode(AppMode.LIGHTBOX); };
  const closeHistory = () => { setMode(previousMode); };
  const openViewer = (idx: number, context: 'HISTORY' | 'VARIATIONS' | 'RESULT') => { setViewingImageIndex(idx); setViewingContext(context); };
  const closeViewer = () => { setViewingImageIndex(null); setViewingContext(null); };
  const navigateHome = () => { if (resultImage) setMode(AppMode.RESULT); else setMode(AppMode.UPLOAD); };

  if (mode === AppMode.LANDING) {
      return <LandingPage onEnter={() => setMode(AppMode.UPLOAD)} />;
  }

  const viewingImages = viewingContext === 'HISTORY' ? historyItems 
                        : viewingContext === 'VARIATIONS' ? variations.map(v => ({ id: v.id, url: v.url, timestamp: Date.now(), label: '', isFavorite: v.isFavorite }))
                        : resultImage ? [{ id: resultId || 'res', url: resultImage, timestamp: Date.now(), label: 'Result', isFavorite: false }] 
                        : [];
  
  const currentViewImage = (viewingImageIndex !== null && viewingImages[viewingImageIndex]) ? viewingImages[viewingImageIndex] : null;

  return (
    <div className="flex flex-col h-[100dvh] bg-background text-white font-sans overflow-hidden">
      
      <AnimatePresence>
          {showOnboarding && <Onboarding onComplete={handleOnboardingComplete} />}
      </AnimatePresence>

      <AnimatePresence>
          {mode === AppMode.LIGHTBOX && (
              <History items={historyItems} onRemove={removeFromHistory} onBulkRemove={handleBulkRemove} onClose={closeHistory} 
                  onView={(url) => { const idx = historyItems.findIndex(i => i.url === url); if(idx !== -1) openViewer(idx, 'HISTORY'); }}
                  onPushProcess={handlePushProcess} onToggleFavorite={toggleFavorite} onBulkFavorite={handleBulkFavorite} onDownload={(url) => handleSmartDownload(url, `reshooter_${Date.now()}.png`)}
              />
          )}
      </AnimatePresence>

      <AnimatePresence>
          {showSubscription && <SubscriptionModal onClose={() => setShowSubscription(false)} />}
      </AnimatePresence>

      {currentViewImage && (
          <ImageViewer src={currentViewImage.url} onClose={closeViewer}
              onNext={() => { if (viewingImageIndex !== null && viewingImageIndex < viewingImages.length - 1) setViewingImageIndex(viewingImageIndex + 1); }}
              onPrev={() => { if (viewingImageIndex !== null && viewingImageIndex > 0) setViewingImageIndex(viewingImageIndex - 1); }}
              hasNext={viewingImageIndex !== null && viewingImageIndex < viewingImages.length - 1} hasPrev={viewingImageIndex !== null && viewingImageIndex > 0}
              isFavorite={favorites.has(currentViewImage.id)} onToggleFavorite={() => toggleFavorite(currentViewImage.id, !favorites.has(currentViewImage.id))}
              onDownload={() => handleSmartDownload(currentViewImage.url, `reshooter_${currentViewImage.id}.png`)}
              onDelete={viewingContext === 'HISTORY' ? () => { if(confirm("Delete image?")) { removeFromHistory(currentViewImage.id); closeViewer(); } } : undefined}
          />
      )}

      {/* --- HEADER --- */}
      <header className="h-16 flex items-center justify-between px-4 md:px-6 border-b border-white/5 bg-background z-50 shrink-0">
        <div className="flex items-center space-x-2 cursor-pointer group" onMouseEnter={() => setIsLogoHovered(true)} onMouseLeave={() => setIsLogoHovered(false)} onClick={navigateHome}>
          <div className="relative">
              <ICONS.Aperture className={clsx("w-6 h-6 text-white transition-transform duration-700", isLogoHovered && "rotate-180")} />
              <div className="absolute inset-0 bg-white/20 blur-lg rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </div>
          <span className="text-sm font-bold tracking-[0.2em] uppercase text-white">Reshooter</span>
        </div>

        <div className="flex items-center space-x-2 md:space-x-4">
            <button onClick={() => setShowSubscription(true)} className="reshoot-counter flex items-center gap-2 px-3 py-1.5 rounded-full bg-surfaceHighlight border border-white/5 hover:border-white/20 transition-all group">
                <ICONS.Zap className="w-3 h-3 text-yellow-500 fill-current group-hover:animate-pulse" />
                <span className="text-[10px] md:text-xs font-mono font-medium text-zinc-300 group-hover:text-white">{reshoots} <span className="hidden xs:inline">RESHOOTS</span></span>
            </button>

            {/* Tutorials Link - Moved to be between Reshoots and Gallery */}
            <button 
                onClick={() => setMode(AppMode.TUTORIALS)}
                className={clsx(
                    "flex items-center gap-2 p-2 md:px-3 md:py-1.5 rounded-full border transition-all text-[10px] font-bold uppercase tracking-widest",
                    mode === AppMode.TUTORIALS ? "bg-white text-black border-white" : "text-zinc-500 hover:text-white border-transparent hover:bg-white/5"
                )}
                title="Academy"
            >
                <ICONS.BookOpen className="w-5 h-5 md:w-3 md:h-3" /> 
                <span className="hidden md:inline">Academy</span>
            </button>
            
            <button onClick={openHistory} className="gallery-btn p-2 text-zinc-400 hover:text-white transition-colors" title="Gallery">
                <ICONS.Layers className="w-5 h-5" />
            </button>
            
            <button onClick={() => setMode(AppMode.PROFILE)} className={clsx("w-8 h-8 rounded-full border flex items-center justify-center overflow-hidden transition-colors", mode === AppMode.PROFILE ? "bg-white border-white text-black" : "bg-surfaceHighlight border-white/10 text-zinc-400 hover:border-white/50")}>
                {user?.photoURL ? <img src={user.photoURL} alt="User" className="w-full h-full object-cover" /> : <ICONS.User className="w-4 h-4" />}
            </button>
        </div>
      </header>

      <main className="flex-1 relative overflow-hidden flex">
        {mode === AppMode.PROFILE && <Profile onOpenSubscription={() => setShowSubscription(true)} />}
        
        {/* TUTORIALS PAGE integration */}
        {mode === AppMode.TUTORIALS && (
            <TutorialsPage onBack={() => setMode(AppMode.UPLOAD)} />
        )}

        {(mode === AppMode.UPLOAD || mode === AppMode.PROCESSING || mode === AppMode.RESULT) && (
             <div className="flex-1 flex flex-col relative">
                 <div className="flex-1 relative bg-surface overflow-hidden flex flex-col w-full">
                     <div className="absolute inset-0 pattern-grid opacity-5 pointer-events-none"></div>
                     <div ref={mobileWrapperRef} className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide relative w-full h-full flex flex-col">
                        {mode === AppMode.UPLOAD && (
                            <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-12 min-h-[500px]">
                                {mainImagePreview ? (
                                    <div className="relative w-full max-w-lg aspect-[3/4] group flex flex-col items-center">
                                         <div className="upload-area w-full h-full rounded-lg overflow-hidden border border-white/10 shadow-2xl bg-black relative z-10">
                                             <img src={mainImagePreview} alt="Preview" className="w-full h-full object-contain" />
                                             <button onClick={() => { setMainImage(null); setMainImagePreview(null); setCritique(null); setShowControls(false); }} className="absolute top-2 right-2 p-2 bg-black/60 text-white rounded-full hover:bg-red-600 transition-colors backdrop-blur-md">
                                                 <ICONS.Close className="w-4 h-4" />
                                             </button>
                                         </div>
                                         <div className="mt-8 flex flex-col items-center gap-4 w-full relative z-20">
                                             <button type="button" onClick={startReshoot} disabled={isProcessing} className="group relative w-full max-sm disabled:opacity-50">
                                                 <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-lg blur opacity-40 group-hover:opacity-100 transition duration-500 animate-tilt"></div>
                                                 <div className="relative flex items-center justify-center gap-3 px-8 py-4 bg-black rounded-lg leading-none border border-white/10 group-hover:text-white transition-colors">
                                                     {isProcessing ? <ICONS.Refresh className="w-5 h-5 text-indigo-400 animate-spin" /> : <ICONS.Sparkles className="w-5 h-5 text-indigo-400 group-hover:text-white transition-colors" />}
                                                     <span className="text-sm font-bold uppercase tracking-[0.2em] bg-clip-text text-transparent bg-gradient-to-r from-white to-zinc-400 group-hover:text-white group-hover:bg-none">
                                                         {isProcessing ? "Processing..." : "Reshoot"}
                                                     </span>
                                                 </div>
                                             </button>
                                             <div className="flex items-center gap-4">
                                                 <button onClick={() => setShowControls(!showControls)} className="customize-btn flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white transition-colors py-2">
                                                     <ICONS.Sliders className="w-3 h-3" />
                                                     {showControls ? "Hide Customization" : "Customize / Manual Control"}
                                                 </button>
                                             </div>
                                         </div>
                                         <div className="w-full mt-4">
                                             <AnimatePresence>
                                                 {showControls && (
                                                     <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="bg-black/40 border border-white/10 rounded-lg p-6 overflow-hidden max-w-2xl mx-auto">
                                                         <Controls config={config} onChange={setConfig} userId={user?.uid} userPlan={userPlan} isAdmin={isAdmin} />
                                                     </motion.div>
                                                 )}
                                             </AnimatePresence>
                                         </div>
                                    </div>
                                ) : (
                                    <div className="text-center space-y-8 animate-in fade-in zoom-in duration-500">
                                        <div className="relative group cursor-pointer">
                                            <div className="absolute -inset-0.5 bg-gradient-to-br from-indigo-500 to-fuchsia-500 rounded-full blur opacity-30 group-hover:opacity-75 transition duration-500"></div>
                                            <label className="upload-area relative flex flex-col items-center justify-center w-64 h-64 rounded-full bg-black border border-white/10 cursor-pointer hover:scale-105 transition-transform duration-300 overflow-hidden">
                                                <div className="absolute inset-0 bg-noise opacity-10"></div>
                                                <ICONS.Plus className="w-12 h-12 text-zinc-500 stroke-[1] mb-2 group-hover:text-white transition-colors" />
                                                <span className="text-xs uppercase tracking-[0.2em] text-zinc-400 group-hover:text-white font-medium">Upload Image</span>
                                                <input type="file" className="hidden" accept="image/*" onChange={handleMainImageUpload} />
                                            </label>
                                        </div>
                                        <p className="text-zinc-500 text-xs tracking-widest uppercase">Supports JPG, PNG • Max 10MB</p>
                                    </div>
                                )}
                            </div>
                        )}
                        {mode === AppMode.RESULT && resultImage && (
                            <div className="flex-1 flex flex-col items-center pt-8 pb-32 md:pb-12 px-4 min-h-screen">
                                <div className="relative w-full max-w-2xl aspect-[3/4] md:aspect-auto md:h-[70vh] mb-8 group">
                                    <ComparisonView before={mainImagePreview} after={resultImage} />
                                    <div className="absolute top-4 right-4 z-30">
                                        <button onClick={(e) => { e.stopPropagation(); setMode(AppMode.UPLOAD); setResultImage(null); }} className="p-2 bg-black/60 hover:bg-red-600/80 text-white rounded-full backdrop-blur-md transition-all active:scale-95 shadow-xl">
                                            <ICONS.Trash className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4 flex-wrap justify-center bg-black/50 backdrop-blur-md p-4 rounded-full border border-white/10 shadow-2xl">
                                    <button onClick={() => handleSmartDownload(resultImage, `reshooter_${Date.now()}.png`)} className="flex items-center gap-2 px-6 py-3 bg-white text-black rounded-full hover:bg-zinc-200 transition-colors font-bold text-xs uppercase tracking-wider">
                                        <ICONS.Download className="w-4 h-4" /> Save
                                    </button>
                                    <button type="button" onClick={startReshoot} disabled={isProcessing} className="flex items-center gap-2 px-6 py-3 bg-black/40 hover:bg-white/10 text-white border border-white/20 rounded-full transition-all font-bold text-xs uppercase tracking-wider disabled:opacity-50">
                                        {isProcessing ? <ICONS.Refresh className="w-4 h-4 animate-spin" /> : <ICONS.Refresh className="w-4 h-4" />}
                                        {isProcessing ? "Processing..." : "Regenerate"}
                                    </button>
                                    <button onClick={handleGenerateVariations} disabled={isGeneratingVariations} className={clsx("flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 transition-all font-bold text-xs uppercase tracking-wider", isGeneratingVariations ? "bg-white/5 text-zinc-500 cursor-wait" : "bg-black/40 hover:bg-white/10 text-white")}>
                                        {isGeneratingVariations ? <ICONS.Refresh className="w-4 h-4 animate-spin" /> : <ICONS.Layers className="w-4 h-4" />}
                                        {isGeneratingVariations ? "Generating..." : "Variations"}
                                    </button>
                                </div>
                            </div>
                        )}
                     </div>
                 </div>
             </div>
        )}
      </main>
    </div>
  );
}

export default App;
